/**
 * The whole scheduling model, and the only place that decides what is due.
 *
 * There are no task *instances*. A task has a frequency and a list of
 * completions; "due" is derived from the last completion every time it is
 * asked for. That is deliberate: a household that misses a week should see one
 * overdue task, not seven queued copies of it, and nothing has to run on a
 * cron to keep the list honest.
 */

export const FREQUENCIES = ["daily", "weekly", "monthly", "seasonal"] as const;
export type Freq = (typeof FREQUENCIES)[number];

/** Nominal period of each frequency, in days. */
export const FREQ_DAYS: Record<Freq, number> = {
  daily: 1,
  weekly: 7,
  monthly: 30,
  seasonal: 90,
};

export const FREQ_LABEL: Record<Freq, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
  seasonal: "Seasonal",
};

/**
 * `never`   — no completion on record; treat as due, but say so differently.
 * `ok`      — done recently enough, nothing to do.
 * `due`     — past its date, inside the tolerance window.
 * `overdue` — past its date by more than the tolerance window.
 */
export type Status = "never" | "ok" | "due" | "overdue";

export interface TaskStatus {
  status: Status;
  /** When it next needs doing. Null when it has never been done. */
  dueAt: Date | null;
  /** Whole days past `dueAt`. 0 when not yet due. */
  daysLate: number;
  /** Whole days until `dueAt`. Null unless status is `ok`. */
  daysUntil: number | null;
  /** Sort key: higher is more urgent. See `sortKey`. */
  urgency: number;
}

export const MS_PER_DAY = 86_400_000;

/**
 * How far past its date a task may drift before it counts as overdue.
 *
 * Half a period, at least one day. A daily task nags the day after it slips; a
 * monthly one gets a fortnight of grace. Without the scaling, everything
 * monthly would sit in `overdue` permanently and the colour would stop meaning
 * anything.
 */
export function toleranceDays(freq: Freq): number {
  return Math.max(1, Math.ceil(FREQ_DAYS[freq] / 2));
}

/** Whole days between two instants, floored — 23h59m is still 0 days. */
export function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / MS_PER_DAY);
}

export function addDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY);
}

/**
 * Urgency ranks the Today list. Bands keep the classes apart no matter how
 * late things get, so a monstrously overdue seasonal task can never outrank
 * today's overdue daily one — the point of the list is what to do now, not
 * what you have failed at longest.
 */
function urgencyOf(status: Status, daysLate: number, freq: Freq): number {
  const band = status === "overdue" ? 3000 : status === "never" ? 2000 : status === "due" ? 1000 : 0;
  if (band === 0) return 0;
  // Within a band: later first, then shorter frequency first.
  const lateness = Math.min(daysLate, 899);
  const shortness = (FREQ_DAYS.seasonal - FREQ_DAYS[freq]) / FREQ_DAYS.seasonal;
  return band + lateness + shortness;
}

/**
 * Status of one task at one instant.
 *
 * `lastDoneAt` is the most recent completion, or null. `now` is always passed
 * in rather than read from the clock so that every caller — routes, tests,
 * metrics — evaluates a whole page against a single consistent instant.
 *
 * `pausedDays` is how many of the days since `lastDoneAt` this task's clock
 * was stopped for (see `trips.ts`): each one pushes the due date out a day, so
 * a fortnight away neither forgives lateness earned before it nor adds any.
 */
export function evaluate(freq: Freq, lastDoneAt: Date | null, now: Date, pausedDays = 0): TaskStatus {
  if (lastDoneAt === null) {
    return {
      status: "never",
      dueAt: null,
      daysLate: 0,
      daysUntil: null,
      urgency: urgencyOf("never", 0, freq),
    };
  }

  const dueAt = addDays(lastDoneAt, FREQ_DAYS[freq] + pausedDays);
  const daysPastDue = daysBetween(dueAt, now);

  if (now < dueAt) {
    // Ceil so "due in 6h" reads as 1 day, not 0 — 0 belongs to `due`.
    const daysUntil = Math.ceil((dueAt.getTime() - now.getTime()) / MS_PER_DAY);
    return { status: "ok", dueAt, daysLate: 0, daysUntil, urgency: 0 };
  }

  const status: Status = daysPastDue >= toleranceDays(freq) ? "overdue" : "due";
  return {
    status,
    dueAt,
    daysLate: daysPastDue,
    daysUntil: null,
    urgency: urgencyOf(status, daysPastDue, freq),
  };
}

/** Everything except `ok` wants doing. */
export function isActionable(status: Status): boolean {
  return status !== "ok";
}

export function isFreq(value: unknown): value is Freq {
  return typeof value === "string" && (FREQUENCIES as readonly string[]).includes(value);
}
